/**
 * Native friendly-name table for the gateway's model catalog.
 *
 * The gateway's `GET /api/models` returns each model's region-stripped bedrock
 * id as its `label` (e.g. `anthropic.claude-opus-4-8`) rather than a curated
 * display name, so the terminal prettifies ids through this table before the
 * picker (and chat-text mentions) render them.
 *
 * These labels live in the package, not in a per-host injection: every host
 * pairs with the same gateway catalog, so the id → name mapping is identical
 * everywhere and a host-level copy would only drift. There is no host override
 * seam — model naming is fully native.
 */

/**
 * Region-agnostic id → display name. Keys carry no region segment
 * (`jp.`/`eu.`/`us.`) and no `amazon-bedrock/` prefix; `defaultModelLabel`
 * normalizes the lookup so both the region-pinned id sent to `/model` and the
 * stripped id from the live catalog resolve to the same name.
 */
const LABELS_BY_ID: ReadonlyMap<string, string> = new Map([
  ['anthropic.claude-opus-4-8', 'Opus 4.8'],
  ['anthropic.claude-fable-5', 'Fable 5'],
  ['anthropic.claude-opus-4-7', 'Opus 4.7'],
  ['anthropic.claude-sonnet-4-6', 'Sonnet 4.6'],
  ['anthropic.claude-haiku-4-5-20251001-v1:0', 'Haiku 4.5'],
  ['deepseek.v3.2', 'DeepSeek 3.2'],
  ['google.gemma-3-27b-it', 'Gemma 3 27B'],
  ['zai.glm-5', 'GLM-5'],
  ['qwen.qwen3-235b-a22b-2507-v1:0', 'Qwen3 235B'],
  ['openai.gpt-oss-120b-1:0', 'GPT-OSS 120B'],
  ['mistral.mistral-large-3-675b-instruct', 'Mistral Large 3'],
]);

/**
 * Drop an `amazon-bedrock/` prefix and any leading two-letter region segment
 * so `jp.anthropic.claude-opus-4-8`, `anthropic.claude-opus-4-8`, and
 * `amazon-bedrock/anthropic.claude-opus-4-8` all key the same entry. Two-letter
 * segments never collide with the catalog's provider prefixes (anthropic,
 * deepseek, google, …), which are all longer than two characters.
 */
function normalizeModelId(id: string): string {
  return id.replace(/^amazon-bedrock\//u, '').replace(/^[a-z]{2}\./u, '');
}

/**
 * Friendly label for a model id, or the id itself when the catalog drifts
 * ahead of this table — so an unknown model still shows *something* rather
 * than being hidden. Callers treat "label === id" as "no friendly name".
 */
export function defaultModelLabel(id: string): string {
  return LABELS_BY_ID.get(normalizeModelId(id)) ?? id;
}
